import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, Globe, Lock } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { SeletorMinisterios } from "@/shared/components/SeletorMinisterios";
import {
  chaveDia,
  combinarDataHora,
  dataHojeInput,
  formatarHora,
  respeitaAntecedenciaMinima,
  separarDataHora,
} from "@/shared/lib/datas";
import type { EventoVisibilidade } from "@/shared/types/database.types";
import type { Ambiente } from "@/modules/reservas/services/ambientesService";
import { reservasService, type ConflitoAmbiente } from "@/modules/reservas/services/reservasService";
import type { DadosEvento, EventoComRelacoes } from "@/modules/eventos/services/eventosService";
import type { MinisterioOpcao } from "@/modules/reservas/components/FormularioReserva";

const CLASSE_INPUT =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
const CLASSE_LABEL = "text-sm font-medium text-text";

interface FormularioEventoProps {
  valoresIniciais?: EventoComRelacoes | null;
  ambientes: Ambiente[];
  ministerios: MinisterioOpcao[];
  /**
   * F4-05 (reteste 22-23/07/2026): só quem gerencia o módulo Eventos pode criar
   * evento PÚBLICO. Para os demais (líder de um ministério não-responsável), a
   * visibilidade fica travada em "Privado" e o ministério é obrigatório.
   */
  podeEventoPublico: boolean;
  carregando: boolean;
  erro?: string | null;
  onSubmit: (dados: DadosEvento) => void;
}

export function FormularioEvento({
  valoresIniciais,
  ambientes,
  ministerios,
  podeEventoPublico,
  carregando,
  erro,
  onSubmit,
}: FormularioEventoProps) {
  const iniInicio = valoresIniciais ? separarDataHora(valoresIniciais.inicio) : null;
  const iniFim = valoresIniciais ? separarDataHora(valoresIniciais.fim) : null;

  const [titulo, setTitulo] = useState(valoresIniciais?.titulo ?? "");
  const [descricao, setDescricao] = useState(valoresIniciais?.descricao ?? "");
  const [visibilidade, setVisibilidade] = useState<EventoVisibilidade>(
    !podeEventoPublico ? "privado" : (valoresIniciais?.visibilidade ?? "publico"),
  );
  const [diaInteiro, setDiaInteiro] = useState(valoresIniciais?.dia_inteiro ?? false);
  // F4-17 (revisão 21/07/2026): evento agora pode durar mais de um dia — data
  // de início e de término são dois campos independentes (antes era um único
  // campo "Data" combinado com a mesma hora início/fim do mesmo dia).
  const [dataInicio, setDataInicio] = useState(iniInicio?.data ?? "");
  const [dataFim, setDataFim] = useState(iniFim?.data ?? iniInicio?.data ?? "");
  const [horaInicio, setHoraInicio] = useState(iniInicio?.hora ?? "");
  const [horaFim, setHoraFim] = useState(iniFim?.hora ?? "");

  // Mantém término >= início ao mudar a data de início (evita intervalo
  // invertido sem precisar de validação de erro pra esse caso comum).
  function mudarDataInicio(novaData: string) {
    setDataInicio(novaData);
    if (dataFim && dataFim < novaData) setDataFim(novaData);
  }
  const multiDia = !!dataInicio && !!dataFim && dataFim !== dataInicio;
  const [ambienteId, setAmbienteId] = useState(valoresIniciais?.ambiente_id ?? "");
  // F2-32: agora suporta mais de um ministério vinculado (atividade conjunta).
  const [ministerioIds, setMinisterioIds] = useState<string[]>(
    valoresIniciais?.ministerios?.map((m) => m.id) ??
      (valoresIniciais?.ministerio_id ? [valoresIniciais.ministerio_id] : []),
  );
  const [conflitos, setConflitos] = useState<ConflitoAmbiente[]>([]);

  function montarIntervalo(): { inicio: string; fim: string } | null {
    if (!dataInicio || !dataFim) return null;
    if (dataFim < dataInicio) return null; // término não pode ser antes do início
    if (diaInteiro) {
      return { inicio: combinarDataHora(dataInicio, "00:00"), fim: combinarDataHora(dataFim, "23:59") };
    }
    if (!horaInicio || !horaFim) return null;
    // No mesmo dia, término tem que ser depois do início; em dias diferentes
    // qualquer combinação de horário serve (ex.: 22h de um dia até 6h do outro).
    if (dataFim === dataInicio && horaFim < horaInicio) return null;
    return { inicio: combinarDataHora(dataInicio, horaInicio), fim: combinarDataHora(dataFim, horaFim) };
  }

  const intervalo = montarIntervalo();
  const minData = dataHojeInput();
  // Regra: não criar no passado; hoje exige antecedência mínima. Dia inteiro
  // valida só a data (não o horário).
  const antecedenciaOk = (() => {
    if (!intervalo) return true;
    if (diaInteiro) return !dataInicio || dataInicio >= minData;
    return respeitaAntecedenciaMinima(intervalo.inicio);
  })();

  useEffect(() => {
    if (!ambienteId || !intervalo) {
      setConflitos([]);
      return;
    }
    let cancelado = false;
    reservasService
      // BUG-04 (auditoria 20/07/2026): ao editar, ignora a própria reserva
      // vinculada ao evento — antes passava `null` sempre, então a reserva do
      // próprio evento contava como conflito ("Ambiente ocupado" falso ao
      // editar o horário/ambiente do evento em edição).
      .verificarConflitos(
        ambienteId,
        intervalo.inicio,
        intervalo.fim,
        valoresIniciais?.reserva_id ?? null,
      )
      .then((res) => {
        if (!cancelado) setConflitos(res);
      })
      .catch(() => {
        if (!cancelado) setConflitos([]);
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambienteId, intervalo?.inicio, intervalo?.fim]);

  const temConflito = !!ambienteId && conflitos.length > 0;
  const primeiroConflito = conflitos[0];

  // F4-17: sugere o próximo horário livre do mesmo ambiente/dia em vez de só
  // bloquear (mesma lógica do formulário de Reserva). Não se aplica a evento
  // de dia inteiro (a duração ocuparia o dia inteiro de qualquer forma) nem
  // a evento de mais de um dia (não há um "mesmo dia" único pra encaixar).
  function proximoHorarioLivre(): { inicio: string; fim: string } | null {
    if (!temConflito || diaInteiro || multiDia || !intervalo) return null;
    const duracaoMs = new Date(intervalo.fim).getTime() - new Date(intervalo.inicio).getTime();
    if (duracaoMs <= 0) return null;
    const fimMaisTardio = conflitos.reduce(
      (max, c) => (new Date(c.fim) > max ? new Date(c.fim) : max),
      new Date(0),
    );
    const propostaFim = new Date(fimMaisTardio.getTime() + duracaoMs);
    if (
      chaveDia(fimMaisTardio.toISOString()) !== dataInicio ||
      chaveDia(propostaFim.toISOString()) !== dataInicio
    ) {
      return null;
    }
    return { inicio: formatarHora(fimMaisTardio.toISOString()), fim: formatarHora(propostaFim.toISOString()) };
  }
  const sugestaoHorario = proximoHorarioLivre();

  function usarSugestao() {
    if (!sugestaoHorario) return;
    setHoraInicio(sugestaoHorario.inicio);
    setHoraFim(sugestaoHorario.fim);
  }
  // F4-04 (revisão 20/07/2026): evento privado exige ao menos um ministério
  // responsável — o banco tem o mesmo travamento (trigger), isto é só para
  // dar o aviso na hora, sem esperar o erro do servidor.
  const precisaMinisterio = visibilidade === "privado" && ministerioIds.length === 0;
  const podeSalvar =
    !!titulo.trim() && !!intervalo && !temConflito && antecedenciaOk && !precisaMinisterio;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!intervalo || !podeSalvar) return;
    onSubmit({
      titulo: titulo.trim(),
      descricao: descricao.trim() || null,
      visibilidade,
      inicio: intervalo.inicio,
      fim: intervalo.fim,
      diaInteiro,
      ambienteId: ambienteId || null,
      ministerioIds,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="evento-titulo" className={CLASSE_LABEL}>
          Título do evento
        </label>
        <input
          id="evento-titulo"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ex.: Culto de Oração"
          className={CLASSE_INPUT}
          required
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="evento-descricao" className={CLASSE_LABEL}>
          Descrição <span className="text-muted">(opcional)</span>
        </label>
        <textarea
          id="evento-descricao"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          rows={2}
          placeholder="Detalhes, orientações, público-alvo…"
          className={CLASSE_INPUT}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={CLASSE_LABEL}>Visibilidade</span>
        {podeEventoPublico ? (
          <>
            <div className="flex gap-2 rounded-lg bg-surface-2 p-1">
              <button
                type="button"
                onClick={() => setVisibilidade("publico")}
                className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-semibold transition-colors ${
                  visibilidade === "publico" ? "bg-surface text-primary shadow-sm" : "text-muted"
                }`}
              >
                <Globe size={15} /> Público
              </button>
              <button
                type="button"
                onClick={() => setVisibilidade("privado")}
                className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-sm font-semibold transition-colors ${
                  visibilidade === "privado" ? "bg-surface text-text shadow-sm" : "text-muted"
                }`}
              >
                <Lock size={15} /> Privado
              </button>
            </div>
            <p className="text-xs text-muted">
              Público aparece no calendário de todos. Privado só para quem participa do ministério
              vinculado — sem ministério escolhido, só você (quem criou) vê o evento.
            </p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm font-semibold text-text">
              <Lock size={15} className="text-muted" /> Privado
              <span className="ml-auto text-[11px] font-normal text-muted">bloqueado</span>
            </div>
            {/* F4-05: líder de ministério não-responsável só cria evento privado. */}
            <p className="text-xs text-muted">
              Só quem gerencia Eventos cria evento público. O seu evento é privado, precisa de
              um ministério seu e fica pendente de aprovação de quem gerencia Eventos.
            </p>
          </>
        )}
      </div>

      <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border bg-surface-2 px-3 py-2.5">
        <span className={CLASSE_LABEL}>Dia inteiro</span>
        <button
          type="button"
          role="switch"
          aria-checked={diaInteiro}
          onClick={() => setDiaInteiro((v) => !v)}
          className={`relative h-6 w-11 rounded-full transition-colors ${diaInteiro ? "bg-primary" : "bg-border"}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-transform ${
              diaInteiro ? "left-0.5 translate-x-5" : "left-0.5"
            }`}
          />
        </button>
      </label>

      {/* F4-17: início e término agora são duas datas independentes — permite
          evento de mais de um dia (ex.: retiro, conferência de fim de
          semana). Deixando iguais, funciona exatamente como antes (evento de
          um dia só). */}
      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="evento-data-inicio" className={CLASSE_LABEL}>
            Início (data)
          </label>
          <input
            id="evento-data-inicio"
            type="date"
            value={dataInicio}
            min={minData}
            onChange={(e) => mudarDataInicio(e.target.value)}
            className={CLASSE_INPUT}
            required
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="evento-data-fim" className={CLASSE_LABEL}>
            Término (data)
          </label>
          <input
            id="evento-data-fim"
            type="date"
            value={dataFim}
            min={dataInicio || minData}
            onChange={(e) => setDataFim(e.target.value)}
            className={CLASSE_INPUT}
            required
          />
        </div>
      </div>
      {multiDia && (
        <p className="-mt-2 text-xs text-info">
          Evento com mais de um dia — vai aparecer em todos os dias do intervalo no Calendário.
        </p>
      )}

      {!diaInteiro && (
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1.5">
            <label htmlFor="evento-inicio" className={CLASSE_LABEL}>
              Hora de início
            </label>
            <input
              id="evento-inicio"
              type="time"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
              className={CLASSE_INPUT}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <label htmlFor="evento-fim" className={CLASSE_LABEL}>
              Hora de término
            </label>
            <input
              id="evento-fim"
              type="time"
              value={horaFim}
              onChange={(e) => setHoraFim(e.target.value)}
              className={CLASSE_INPUT}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="evento-ambiente" className={CLASSE_LABEL}>
          Ambiente <span className="text-muted">(opcional)</span>
        </label>
        <select
          id="evento-ambiente"
          value={ambienteId}
          onChange={(e) => setAmbienteId(e.target.value)}
          className={CLASSE_INPUT}
        >
          <option value="">Sem ambiente definido</option>
          {ambientes.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nome}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted">
          Ao escolher um ambiente, o sistema o reserva automaticamente e checa conflitos.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={CLASSE_LABEL}>
          Ministério{visibilidade === "privado" ? " responsável" : " responsável (opcional)"}{" "}
          <span className="text-muted">(pode marcar mais de um)</span>
        </span>
        {/* F2-40 (protótipo aprovado 24-25/07/2026): antes era uma lista
            fixa de checkboxes numa caixinha com scroll — trocado por busca ao
            vivo + chips removíveis (SeletorMinisterios). A lista já vem
            restrita pelo chamador — quem não gerencia o módulo só vê os
            ministérios que lidera. */}
        <SeletorMinisterios
          id="evento-ministerios"
          ministerios={ministerios}
          selecionados={ministerioIds}
          onChange={setMinisterioIds}
        />
        {/* UX-09: semântica de "privado" + ministério confundia o usuário — deixa
            explícito quem vai enxergar o evento antes de salvar. */}
        {visibilidade === "privado" ? (
          <p
            role={precisaMinisterio ? "alert" : undefined}
            aria-live="polite"
            className={`text-xs ${precisaMinisterio ? "font-medium text-danger" : "text-muted"}`}
          >
            {precisaMinisterio
              ? "Evento privado precisa de pelo menos um ministério responsável."
              : "Privado: só quem participa dos ministérios marcados acima verá o evento."}
          </p>
        ) : (
          <p className="text-xs text-muted">
            Sem ministério marcado, o evento fica sob responsabilidade da administração da igreja.
          </p>
        )}
      </div>

      {temConflito && primeiroConflito && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-2 rounded-lg bg-danger/10 px-3 py-2.5 text-sm text-danger"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <b>Ambiente ocupado neste horário.</b> Já existe reserva das{" "}
            {formatarHora(primeiroConflito.inicio)} às {formatarHora(primeiroConflito.fim)}. Ajuste o
            horário ou o ambiente.
            {/* F4-17: sugestão de horário alternativo em vez de só bloquear. */}
            {sugestaoHorario ? (
              <button
                type="button"
                onClick={usarSugestao}
                className="mt-2 block text-xs font-semibold text-danger underline hover:no-underline"
              >
                Usar próximo horário livre: {sugestaoHorario.inicio}–{sugestaoHorario.fim}
              </button>
            ) : (
              !diaInteiro &&
              (multiDia ? (
                // F4-18 (reteste): aviso mais claro para evento de vários dias.
                <p role="alert" aria-live="polite" className="mt-1 text-xs text-danger/80">
                  Para eventos de vários dias, confira se o ambiente está livre em TODO o período —
                  ajuste as datas ou escolha outro ambiente.
                </p>
              ) : (
                <p role="alert" aria-live="polite" className="mt-1 text-xs text-danger/80">
                  Não há mais horário livre para esse ambiente neste dia — tente outro dia ou
                  ambiente.
                </p>
              ))
            )}
          </div>
        </div>
      )}

      {intervalo && !antecedenciaOk && (
        <p role="alert" aria-live="polite" className="text-xs font-medium text-danger">
          Não é possível criar um evento em data/horário que já passou. Para hoje, agende com pelo
          menos 1h de antecedência.
        </p>
      )}

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button type="submit" carregando={carregando} disabled={!podeSalvar} className="mt-1">
        {valoresIniciais ? "Salvar alterações" : "Criar evento"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
